import { inject, Injectable, OnDestroy, Optional, SkipSelf, type Provider, APP_INITIALIZER } from '@angular/core';
import { Router, type Event as Event$, NavigationStart, NavigationEnd, ActivationEnd } from '@angular/router';
import { type Observable, Subscription, BehaviorSubject } from 'rxjs';
import { filter, tap, take, skipUntil, concatMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class BrowserNavigationService implements OnDestroy {
  private readonly _router: Router = inject(Router);
  private readonly _subscription: Subscription = new Subscription();

  private readonly _isReloadSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private get _isReload(): boolean {
    return this._isReloadSubject.getValue();
  }
  private set _isReload(isReload: boolean) {
    this._isReloadSubject.next(isReload);
  }

  private readonly _isBackOrforwardSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private get _isBackOrforward(): boolean {
    return this._isBackOrforwardSubject.getValue();
  }
  private set _isBackOrforward(isBackOrforward: boolean) {
    this._isBackOrforwardSubject.next(isBackOrforward);
  }

  private readonly _isPopstateSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private get _isPopstate(): boolean {
    return this._isPopstateSubject.getValue();
  }
  private set _isPopstate(isPopstate: boolean) {
    this._isPopstateSubject.next(isPopstate);
  }

  private readonly _isDirectAccessSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private get _isDirectAccess(): boolean {
    return this._isDirectAccessSubject.getValue();
  }
  private set _isDirectAccess(isDirectAccess: boolean) {
    this._isDirectAccessSubject.next(isDirectAccess);
  }

  public constructor(@Optional() @SkipSelf() private readonly _self?: BrowserNavigationService) {
    if (_self) {
      throw Error(`[BrowserNavigationService]: this service should be a singleton.`);
    }
  }

  public init() {
    const naviType: NavigationTimingType = window.performance
      .getEntriesByType('navigation')
      .map((e: PerformanceEntry) => (e as PerformanceNavigationTiming).type)[0];
    this._isBackOrforward = naviType === 'back_forward';
    this._isReload = naviType === 'reload';
    this._detectNextNavigation();
    this._watchEvents();
  }

  public isBackOrforward(): boolean {
    return this._isBackOrforward;
  }

  public isReload(): boolean {
    return this._isReload;
  }

  public isDirectAccess(): boolean {
    return !this._isBackOrforward && !this._isReload && this._isDirectAccess;
  }

  private _detectNextNavigation() {
    const events$ = this._router.events;
    const start$ = events$.pipe(filter((e): e is NavigationStart => e instanceof NavigationStart));
    const end$ = events$.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd));
    const next$ = start$.pipe(
      skipUntil(end$),
      tap(() => {
        this._isDirectAccess = false;
        this._isReload = false;
        this._isBackOrforward = false;
      })
    );

    this._subscription.add(next$.subscribe());
  }

  private _watchEvents() {
    const events$: Observable<Event$> = this._router.events;
    const start$ = events$.pipe(
      filter((e): e is NavigationStart => e instanceof NavigationStart),
      tap(({ navigationTrigger: trigger }) => {
        this._isPopstate = trigger === 'popstate';
      })
    );
    const end$ = events$.pipe(
      filter((e): e is ActivationEnd => e instanceof ActivationEnd),
      filter(() => this._isPopstate),
      tap(() => {
        this._isBackOrforward = true;
      })
    );

    this._subscription.add(start$.subscribe());
    this._subscription.add(end$.subscribe());
  }

  public ngOnDestroy() {
    this._isReloadSubject.complete();
    this._isBackOrforwardSubject.complete();
    this._isPopstateSubject.complete();
    this._isDirectAccessSubject.complete();
    this._subscription.unsubscribe();
  }
}

const factory = (svc: BrowserNavigationService) => () => svc.init();
export const BROWSER_NAVIGATION_INITIALIZER_PROVIDER: Provider = {
  provide: APP_INITIALIZER,
  useFactory: factory,
  deps: [BrowserNavigationService],
  multi: true,
};